//markers
import React, { useRef, useState } from "react";
import "./event.css";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import dayjs from "dayjs";

gsap.registerPlugin(ScrollTrigger);



const Events = ({ events,theme }) => {
  const sectionRef = useRef(null);

  const [showPopup, setShowPopup] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const openPopup = (event) => {
    setSelectedEvent(event);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedEvent(null);
  };


useGSAP(() => {

  const section = sectionRef.current;

  if (!section) return;


  const items = gsap.utils.toArray(
    ".timeline-item",
    section
  );

  const singleEventCard =
    section.querySelector(".single-event-card");

  const line =
    section.querySelector(".timeline-line-fill");

  const lineContainer =
    section.querySelector(".timeline-line");



  const lineEndPadding = 55;


  const updateTimelineLine = () => {

    const timeline =
      section.querySelector(".timeline");


    if (
      !timeline ||
      !lineContainer ||
      items.length === 0
    ) {
      return;
    }


    const dots = items
      .map((item) =>
        item.querySelector(".timeline-dot")
      )
      .filter(Boolean);


    if (dots.length === 0) {
      return;
    }


    const timelineRect =
      timeline.getBoundingClientRect();

    const firstDotRect =
      dots[0].getBoundingClientRect();

    const lastDotRect =
      dots[dots.length - 1].getBoundingClientRect();



    const firstCenter =
      firstDotRect.top +
      firstDotRect.height / 2 -
      timelineRect.top;


    const lastCenter =
      lastDotRect.top +
      lastDotRect.height / 2 -
      timelineRect.top;


    const lineHeight =
      lastCenter -
      firstCenter +
      lineEndPadding;


    gsap.set(lineContainer, {

      top: firstCenter,

      height: Math.max(
        0,
        lineHeight
      )

    });

  };


  const eventCards =
    section.querySelectorAll(".event-card");


  gsap.set(eventCards, {

    opacity: 0,

    y: 60

  });

  if (singleEventCard) {

    const singleTrigger =
      ScrollTrigger.create({

        trigger: singleEventCard,

        // The actual card controls the trigger
        start: "top 80%",

        once: true,


        onEnter: () => {

          gsap.to(singleEventCard, {

            opacity: 1,

            y: 0,

            duration: 0.7,

            ease: "power2.out"

          });

        }

      });


    requestAnimationFrame(() => {

      ScrollTrigger.refresh();

    });


    return () => {

      singleTrigger.kill();

    };

  }


  if (
    !line ||
    !lineContainer ||
    items.length === 0
  ) {
    return;
  }


  gsap.set(line, {

    scaleY: 0,

    transformOrigin: "top center"

  });


  gsap.set(
    section.querySelectorAll(".heart-outline"),
    {

      opacity: 1

    }
  );


  gsap.set(
    section.querySelectorAll(".heart-filled"),
    {

      opacity: 0,

      scale: 0.7,

      transformOrigin: "center center"

    }
  );



  updateTimelineLine();


  // =========================================
  // TIMELINE EVENTS
  // =========================================

  const triggers = [];


  items.forEach((item, index) => {

    // -----------------------------------------
    // Event elements
    // -----------------------------------------

    const card =
      item.querySelector(".event-card");

    const heart =
      item.querySelector(".timeline-dot");

    const outlineHeart =
      heart?.querySelector(".heart-outline");

    const filledHeart =
      heart?.querySelector(".heart-filled");


    // -----------------------------------------
    // Safety check
    // -----------------------------------------

    if (
      !card ||
      !heart ||
      !outlineHeart ||
      !filledHeart
    ) {
      return;
    }


    // =========================================
    // CHECK IF THIS IS LAST EVENT
    // =========================================

    const isLastEvent =
      index === items.length - 1;


    // =========================================
    // CREATE SCROLL TRIGGER
    // =========================================

    const trigger =
      ScrollTrigger.create({

        // ---------------------------------------
        // IMPORTANT
        //
        // Every event triggers itself.
        // ---------------------------------------

        trigger: item,


        // ---------------------------------------
        // Trigger when event reaches
        // approximately 80% of viewport.
        // ---------------------------------------

        start: "top 80%",


        // ---------------------------------------
        // Run only once
        // ---------------------------------------

        once: true,


        // =======================================
        // EVENT ENTER
        // =======================================

        onEnter: () => {


          // -------------------------------------
          // Get current positions
          // -------------------------------------

          const heartRect =
            heart.getBoundingClientRect();

          const lineRect =
            lineContainer.getBoundingClientRect();


          // -------------------------------------
          // Heart center
          // -------------------------------------

          const heartCenter =
            heartRect.top +
            heartRect.height / 2;


          // =====================================
          // CALCULATE LINE PROGRESS
          // =======================================
          //
          // For normal events:
          //
          //     line → event heart
          //
          // For final event:
          //
          //     line → actual bottom
          //
          // =======================================

          let progress =
            (
              heartCenter -
              lineRect.top
            ) /
            lineRect.height;


          // -------------------------------------
          // Last event
          //
          // Fill entire extended line.
          // -------------------------------------

          if (isLastEvent) {

            progress = 1;

          }


          // -------------------------------------
          // Safety clamp
          // -------------------------------------

          const clampedProgress =
            gsap.utils.clamp(
              0,
              1,
              progress
            );


          // =====================================
          // EVENT ANIMATION
          // =====================================

          const tl =
            gsap.timeline();


          // -------------------------------------
          // 1. Card appears
          // -------------------------------------

          tl.to(card, {

            opacity: 1,

            y: 0,

            duration: 0.7,

            ease: "power2.out"

          })


          // -------------------------------------
          // 2. Timeline grows
          // -------------------------------------

          .to(line, {

            scaleY: clampedProgress,

            duration: 0.55,

            ease: "power2.out"

          }, "<")


          // -------------------------------------
          // 3. Outline heart disappears
          // -------------------------------------

          .to(outlineHeart, {

            opacity: 0,

            duration: 0.18

          }, "<")


          // -------------------------------------
          // 4. Filled heart appears
          // -------------------------------------

          .to(filledHeart, {

            opacity: 1,

            scale: 1.18,

            duration: 0.22,

            ease: "back.out(2)"

          }, "<")


          // -------------------------------------
          // 5. Heart settles
          // -------------------------------------

          .to(filledHeart, {

            scale: 1,

            duration: 0.18

          });

        }

      });


    // -----------------------------------------
    // Store trigger for cleanup
    // -----------------------------------------

    triggers.push(trigger);

  });


  // =========================================
  // REFRESH HANDLER
  // =========================================

  const handleRefresh = () => {

    updateTimelineLine();

  };


  ScrollTrigger.addEventListener(
    "refresh",
    handleRefresh
  );


  // =========================================
  // RESIZE HANDLER
  // =========================================

  let resizeTimeout;


  const handleResize = () => {

    clearTimeout(resizeTimeout);


    resizeTimeout = setTimeout(() => {

      updateTimelineLine();

      ScrollTrigger.refresh();

    }, 100);

  };


  window.addEventListener(
    "resize",
    handleResize
  );


  // =========================================
  // INITIAL REFRESH
  // =========================================
  //
  // Two RAFs give the browser time to finish
  // fonts/layout before measuring positions.
  //
  // =========================================

  requestAnimationFrame(() => {

    requestAnimationFrame(() => {

      updateTimelineLine();

      ScrollTrigger.refresh();

    });

  });


  // =========================================
  // CLEANUP
  // =========================================

  return () => {

    // -----------------------------------------
    // Clear resize timeout
    // -----------------------------------------

    clearTimeout(resizeTimeout);


    // -----------------------------------------
    // Remove resize listener
    // -----------------------------------------

    window.removeEventListener(
      "resize",
      handleResize
    );


    // -----------------------------------------
    // Remove ScrollTrigger refresh listener
    // -----------------------------------------

    ScrollTrigger.removeEventListener(
      "refresh",
      handleRefresh
    );


    // -----------------------------------------
    // Kill all event triggers
    // -----------------------------------------

    triggers.forEach((trigger) => {

      trigger.kill();

    });

  };


}, {
  scope: sectionRef
});


  const getOrdinal = (day) => {
    if (day > 3 && day < 21) return "th";

    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatEventDate = (date) => {
    if (!date) return "";

    const parsedDate = dayjs(date);
    const day = parsedDate.date();

    return `${day}${getOrdinal(day)} ${parsedDate.format("MMMM, YYYY")}`;
  };

  const getEventDay = (date) => {
    if (!date) return "";

    return dayjs(date).format("dddd").toUpperCase();
  };

  const getGoogleMapsUrl = (gLocation) => {
    if (!gLocation?.latitude || !gLocation?.longitude) {
      return "#";
    }

    return `https://www.google.com/maps/search/?api=1&query=${gLocation.latitude},${gLocation.longitude}`;
  };

  return (
    <>
      <section className={`event-section ${theme || ''}`} ref={sectionRef}>

        <h1 className="event-section-heading" data-aos="fade-up">
          The Celebrations
        </h1>

{events.length === 0 ? (

  // =========================
  // NO EVENTS
  // =========================
  <div className="no-events">

    <div className="no-events-icon">
      ♡
    </div>

    <h2>No events added yet</h2>

    <p>
      Your celebrations will appear here once you add an event.
    </p>

  </div>

) : events.length === 1 ? (

  // =========================
  // SINGLE EVENT
  // =========================

  events.map((event) => {

    const hasEventDetails =
      event.eventTheme?.trim() || event.eventInfo?.trim();

    return (
      <div
        key={event._id}
        className="single-event-wrapper"
      >

        <div className="event-card single-event-card">

          <div className="event-top">

            <h2 className="event-title">
              {event.eventName}
            </h2>

            <span className="event-time">
              {event.eventTime}
            </span>

          </div>

          <div className="event-date-row">

            <span className="event-date">
              {formatEventDate(event.eventDate)}
            </span>

            <span className="event-day">
              {getEventDay(event.eventDate)}
            </span>

          </div>

          <p className="event-address">
            {event.eventAddress}
          </p>

          <div className="event-buttons">

            {event.gLocation?.latitude &&
              event.gLocation?.longitude && (
                <a
                  href={getGoogleMapsUrl(event.gLocation)}
                  target="_blank"
                  rel="noreferrer"
                  className="event-btn"
                >
                  Google Maps
                </a>
              )}

            {hasEventDetails && (
              <button
                className="event-btn"
                type="button"
                onClick={() => openPopup(event)}
              >
                View More Details
              </button>
            )}

          </div>

        </div>

      </div>
    );
  })

) : (

  // =========================
  // MULTIPLE EVENTS
  // =========================

   <div className="timeline">

    {/* Timeline line */}
    <div className="timeline-line">
      <div className="timeline-line-fill"></div>
    </div>

    {events.map((event, index) => {

      const side = index % 2 === 0 ? "right" : "left";

      const hasEventDetails =
        event.eventTheme?.trim() || event.eventInfo?.trim();

      return (
        <div
          key={event._id}
          className={`timeline-item ${side}`}
          data-index={index}
        >

          {/* Timeline Heart */}
          <div className="timeline-dot">

<svg
  className="heart-outline"
  width="22"
  height="22"
  viewBox="0 0 24 24"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.03L12 21.35Z"
    fill="var(--event-section-bg-color)"
    stroke="var(--event-heart-outline-color)"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
</svg>

            {/* Filled Heart */}
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              className="heart-filled"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.03L12 21.35Z"
                fill="var(--event-heart-filled-color)"
              />
            </svg>

          </div>

          {/* Event Card */}
          <div className="event-card">

            {/* Heading + Time */}
            <div className={`event-top ${side}`}>

              <h2 className="event-title">
                {event.eventName}
              </h2>

              <span className="event-time">
                {event.eventTime}
              </span>

            </div>

            {/* Date + Day */}
            <div className={`event-date-row ${side}`}>

              <span className="event-date">
                {formatEventDate(event.eventDate)}
              </span>

              <span className="event-day">
                {getEventDay(event.eventDate)}
              </span>

            </div>

            {/* Venue */}
            <p className={`event-address ${side}`}>
              {event.eventAddress}
            </p>

            {/* Buttons */}
            <div className={`event-buttons ${side}`}>

              {event.gLocation?.latitude &&
                event.gLocation?.longitude && (
                  <a
                    href={getGoogleMapsUrl(event.gLocation)}
                    target="_blank"
                    rel="noreferrer"
                    className="event-btn"
                  >
                    Get Directions
                  </a>
                )}

              {hasEventDetails && (
                <button
                  className="event-btn"
                  type="button"
                  onClick={() => openPopup(event)}
                >
                  Event Details
                </button>
              )}

            </div>

          </div>

        </div>
      );
    })}

  </div>
)}

 {showPopup && (
        <div
          className="event-popup-overlay"
          onClick={closePopup}
        >

          <div
            className="event-popup"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              className="popup-close"
              onClick={closePopup}
            >
              ✕
            </button>

            <h2 className="popup-title">
              Event Details
            </h2>

          {selectedEvent?.eventTheme?.trim() && (
  <div className="popup-section">
    <h3>Dress Code</h3>
    <p>{selectedEvent.eventTheme}</p>
  </div>
)}

{selectedEvent?.eventTheme?.trim() &&
  selectedEvent?.eventInfo?.trim() && (
    <div className="popup-divider"></div>
)}

{selectedEvent?.eventInfo?.trim() && (
  <div className="popup-section">
    <h3>About Event</h3>
    <p>{selectedEvent.eventInfo}</p>
  </div>
)}

          </div>

        </div>
      )}

      </section>
     
    </>
  );
};

export default Events;